import { fetchImageSearchTerms } from "api/businesses";
import {
  Paths,
  Permissions,
  PlanRestrictions,
  Realm,
  Subscription,
  UpdateUserBody,
  User,
  UserResponse,
} from "api/config/chalice-types";
import {
  connectSocial,
  deleteIntegration,
  updateIntegration,
} from "api/integrations";
import { removeMember, updatePermissions } from "api/teams";
import {
  fetchRealm,
  fetchUserDetails,
  updateRealm,
  updateUser,
} from "api/user";
import { createCompletedMilestonesDict } from "hooks/useOnboardingTracking";
import { updateBusinessDetails } from "store/business/businessActions";
import { Business } from "store/business/businessConstants";
import { fetchPostsQuotaThunk } from "store/posts/postActions";
import { PostCommentData } from "store/posts/postConstants";
import { createAppAsyncThunk } from "store/storeHelpers";
import { Integration, UserInfo } from "./userConstants";
import { removeTeamMember, updateCurrentBusiness } from "./userSlice";

export type TypesafeChaliceUser = User & {
  id: string;
  invoice_data: {
    current_period_end?: number;
    next_invoice_amount?: string;
  };
  subscription: Subscription & {
    is_appsumo?: boolean;
    // TODO: BE to add Subscription.restrictions to swagger def and use that instead
    restrictions?: PlanRestrictions;
  };
  is_agency?: boolean;
};

export type TypesafeUserResponse = UserResponse & {
  businesses: Business[];
  user: TypesafeChaliceUser;
};

export const getUserDetails = createAppAsyncThunk<TypesafeUserResponse>(
  "posts/getUserDetails",
  async (_, { dispatch }) => {
    const userResponse = await fetchUserDetails();
    await dispatch(fetchPostsQuotaThunk());
    return userResponse;
  }
);

export const updateUserDetails = createAppAsyncThunk(
  "posts/updateUserDetails",
  (payload: UpdateUserBody) => updateUser(payload) as Promise<UserInfo>
);

export const updateRealmDetails = createAppAsyncThunk(
  "posts/updateRealmDetails",
  (payload: Realm) => updateRealm(payload)
);

export const fetchRealmDetails = createAppAsyncThunk(
  "posts/fetchRealmDetails",
  (payload: string) => fetchRealm(payload)
);

export const fetchSearchTerms = createAppAsyncThunk(
  "posts/fetchSearchTerms",
  async (_: undefined, { getState }) => {
    const businessId = getState().user.currentBusiness.id;
    const user = await fetchImageSearchTerms(businessId);
    return user;
  }
);

export const addIntegration = createAppAsyncThunk(
  "user/addIntegration",
  async (
    data: Omit<Paths.V1CreateIntegration.RequestBody, "business_id">,
    { getState, dispatch }
  ) => {
    const { currentBusiness } = getState().user;
    const { integration } = await connectSocial({
      ...data,
      business_id: currentBusiness.id,
    });

    await dispatch(
      updateBusinessDetails({
        onboarding_tracking: createCompletedMilestonesDict(
          "hasConnectedIntegration"
        ),
      })
    );

    integration &&
      dispatch(
        updateCurrentBusiness({
          integrations: currentBusiness.integrations.concat(
            integration as Integration
          ),
        })
      );

    return integration;
  }
);

export const updateIntegrationSettings = createAppAsyncThunk(
  "user/updateIntegrationSettings",
  async (
    {
      id,
      ...data
    }: Pick<Integration, "id" | "publish_settings" | "selected_page">,
    { getState, dispatch }
  ) => {
    const { integration: updatedIntegration } = await updateIntegration(
      id,
      data
    );
    const { integrations } = getState().user.currentBusiness;

    updatedIntegration &&
      dispatch(
        updateCurrentBusiness({
          integrations: integrations.map((integration) =>
            integration.id === id
              ? (updatedIntegration as Integration)
              : integration
          ),
        })
      );
  }
);

export const removeIntegration = createAppAsyncThunk(
  "user/removeIntegration",
  async (id: string, { getState, dispatch }) => {
    await deleteIntegration(id);

    const { integrations } = getState().user.currentBusiness;

    dispatch(
      updateCurrentBusiness({
        integrations: integrations.filter(
          (integration) => integration.id !== id
        ),
      })
    );
  }
);

export const updateTeamMemberPermissions = createAppAsyncThunk(
  "user/updateTeamMemberPermissions",
  async (
    { roleId, permissions }: { roleId: string; permissions: Permissions },
    { getState, dispatch }
  ) => {
    const { currentBusiness } = getState().user;

    const { role } = await updatePermissions({
      businessId: currentBusiness.id,
      roleId,
      permissions,
    });

    role?.permissions &&
      dispatch(
        updateCurrentBusiness({
          members: currentBusiness.members.map((member) =>
            member.role_id === roleId
              ? { ...member, permissions: role.permissions! }
              : member
          ),
        })
      );
  }
);

export const removeTeamMemberFromBusiness = createAppAsyncThunk(
  "user/removeTeamMemberFromBusiness",
  async (
    { businessId, roleId }: { businessId: string; roleId: string },
    { dispatch }
  ) => {
    await removeMember({
      businessId,
      roleId,
    });

    dispatch(removeTeamMember({ businessId, roleId }));
  }
);

export const getFullName = (
  user: PostCommentData | User,
  { fallbackToEmail, fallbackName } = {
    fallbackToEmail: true,
    fallbackName: "Guest",
  }
) =>
  user.given_name
    ? `${user?.given_name?.trim()} ${user?.family_name?.trim()}`.trim()
    : fallbackToEmail && "email" in user && user.email
      ? user.email
      : fallbackName;
